'use client';

import type { ClientDictionary } from '@/dictionaries/client-sections';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';

const DictsContext = createContext<Partial<ClientDictionary> | undefined>(
  undefined,
);

// Route-only sections (see EXPERIENCES_CLIENT_DICTIONARY_SECTIONS) exist only inside their
// nested provider; reading them elsewhere returns undefined.
export const useTranslation = () => {
  const context = useContext(DictsContext);
  if (!context) {
    throw new Error('useTranslation must be used within a DictionaryProvider');
  }
  return context as ClientDictionary;
};

// Nested providers add sections on top of the parent ones
export const DictionaryProvider: React.FC<{
  dicts: Partial<ClientDictionary>;
  children: ReactNode;
}> = ({ dicts, children }) => {
  const parent = useContext(DictsContext);
  const value = useMemo(() => ({ ...parent, ...dicts }), [parent, dicts]);

  return (
    <DictsContext.Provider value={value}>{children}</DictsContext.Provider>
  );
};
